import React, { useEffect, useState } from "react";

// Activity Log V1 — the unified, folder/team-scoped activity stream rendered in
// the left ACTIVITY tab (governance spec). One chronological feed of ALL parties'
// actions, newest first, ordered by system ingestion sequence (system_seq), with
// each entry identity-bound by the actor's frozen folder-scoped colour + name
// snapshot. Readable by the folder owner and current collaborators.

const PAGE = 50;

interface AuditEventView {
  id: string;
  event_type: string;
  is_critical: boolean;
  created_at: string;
  actor_timezone: string | null;
  message_key: string;
  system_seq: number | string | null;
  actor_name: string | null;
  actor_color: string | null;
  actor_role: string | null;
  actor_is_viewer: boolean;
  subject_is_viewer: boolean;
  subject_name: string | null;
  style_number: string | null;
  email: string | null;
}

const isZh = (lang: string) => lang === "zh";

function actorLabel(e: AuditEventView, lang: string): string {
  if (e.actor_name) return e.actor_name;
  if (e.actor_is_viewer) return isZh(lang) ? "你" : "You";
  if (e.actor_role === "OWNER") return isZh(lang) ? "团队负责人" : "The team leader";
  return isZh(lang) ? "一位协作者" : "A collaborator";
}

function subjectLabel(e: AuditEventView, lang: string): string {
  if (e.subject_is_viewer) return isZh(lang) ? "你" : "you";
  if (e.subject_name) return e.subject_name;
  if (e.email) return e.email;
  return isZh(lang) ? "一位协作者" : "a collaborator";
}

function styleLabel(e: AuditEventView, lang: string): string {
  return e.style_number ? `"${e.style_number}"` : (isZh(lang) ? "一个款号" : "a tech pack");
}

// Action phrasing. Actor is shown separately (coloured name); this returns the
// predicate + subject only, so the rendered line reads "<Actor> <predicate>".
function predicate(e: AuditEventView, lang: string): string {
  const s = subjectLabel(e, lang);
  const st = styleLabel(e, lang);
  if (isZh(lang)) {
    switch (e.event_type) {
      case "member_removed":   return `将 ${s} 移出了共享文件夹。`;
      case "invite_created":   return `邀请了 ${s}。`;
      case "invite_accepted":  return `接受了邀请。`;
      case "invite_cancelled": return `取消了对 ${s} 的邀请。`;
      case "pack_ejected":     return `将 ${st}（由 ${s} 贡献）移出了共享文件夹。`;
      case "pack_added_to_folder": return `将 ${st} 添加到了共享文件夹。`;
      case "pack_removed":     return `删除了 ${st}。`;
      case "unshare":          return `关闭了此文件夹的共享。`;
      case "delete_denied":    return `尝试删除 ${e.style_number ? st : "某项内容"}，但被拒绝（仅限所有者）。`;
      case "pack_saved":       return `保存了此款号的修改。`;
      case "spec_updated":
      case "field_updated":    return `更新了此款号的内容。`;
      case "image_uploaded":   return `上传了一张图片。`;
      case "ai_spec_generated": return `用 AI 生成了规格。`;
      case "pack_submitted":   return `提交了此款号。`;
      case "pack_decision":    return `记录了一项决定。`;
      default:                 return e.message_key;
    }
  }
  switch (e.event_type) {
    case "member_removed":   return `removed ${s} from the shared folder.`;
    case "invite_created":   return `invited ${s}.`;
    case "invite_accepted":  return `accepted the invitation.`;
    case "invite_cancelled": return `cancelled the invitation to ${s}.`;
    case "pack_ejected":     return `removed ${st} (contributed by ${s}) from the shared folder.`;
    case "pack_added_to_folder": return `added ${st} to the shared folder.`;
    case "pack_removed":     return `deleted ${st}.`;
    case "unshare":          return `turned off sharing for this folder.`;
    case "delete_denied":    return `attempted to delete ${e.style_number ? st : "an item"} — denied (owner only).`;
    case "pack_saved":       return `saved changes to this tech pack.`;
    case "spec_updated":
    case "field_updated":    return `updated this tech pack.`;
    case "image_uploaded":   return `uploaded an image.`;
    case "ai_spec_generated": return `generated a spec with AI.`;
    case "pack_submitted":   return `submitted this tech pack.`;
    case "pack_decision":    return `logged a decision.`;
    default:                 return e.message_key;
  }
}

function formatTime(e: AuditEventView, lang: string): string {
  const d = new Date(e.created_at);
  const locale = isZh(lang) ? "zh-CN" : "en-US";
  const viewer = d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  if (!e.actor_timezone) return viewer;
  try {
    const actor = new Intl.DateTimeFormat(locale, {
      timeStyle: "short", timeZoneName: "short", timeZone: e.actor_timezone,
    }).format(d);
    return `${viewer} · ${actor}`;
  } catch { return viewer; }
}

// Cross-remount cache (the Surface panel remounts on editor enter/exit). Show the
// last-seen rows INSTANTLY on remount, then revalidate silently — so navigation
// never flashes an empty / loading state. Keyed by `scope:id`.
const streamCache = new Map<string, AuditEventView[]>();

// scope="folder" → Surface/Folder Layer Activity Section (folder-governance).
// scope="pack"   → TP-Layer Activity Section (current-pack content events).
export function FolderActivityStream({ scope, id, lang }: { scope: "folder" | "pack"; id: string | null; lang: string }) {
  const [events,      setEvents]      = useState<AuditEventView[]>([]);
  const [nextCursor,  setNextCursor]  = useState<string | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(false);

  // `background` = a silent poll/focus refetch: never shows loading, never clears
  // the list, never surfaces errors. Stale-while-revalidate — only PREPEND
  // genuinely-new events (higher system_seq); if nothing is new, keep the SAME
  // array reference so React does not re-render/blink (Issue 2).
  async function load(cursor: string | null, background = false) {
    if (cursor) setLoadingMore(true);
    else if (!background) { setLoading(true); setError(false); }
    try {
      const { getFreshToken } = await import("./auth.js");
      const token = await getFreshToken();
      if (!token) throw new Error("no token");
      const qs = new URLSearchParams({ limit: String(PAGE) });
      if (cursor) qs.set("before", cursor);
      const base = scope === "pack" ? `/api/studio/packs/${id}/audit` : `/api/studio/folders/${id}/audit`;
      const res = await fetch(`${base}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json() as { events: AuditEventView[]; next_cursor: string | null };
      if (cursor) {
        setEvents(prev => { const next = [...prev, ...data.events]; streamCache.set(`${scope}:${id}`, next); return next; });
        setNextCursor(data.next_cursor);
      } else if (background) {
        setEvents(prev => {
          let next: AuditEventView[];
          if (prev.length === 0) next = data.events;
          else {
            const topSeq = Number(prev[0].system_seq ?? 0);
            const fresh = data.events.filter(e => Number(e.system_seq ?? 0) > topSeq);
            next = fresh.length ? [...fresh, ...prev] : prev;   // same ref → no blink
          }
          streamCache.set(`${scope}:${id}`, next);
          return next;
        });
      } else {
        setEvents(data.events);
        setNextCursor(data.next_cursor);
        streamCache.set(`${scope}:${id}`, data.events);
      }
      if (!background) setError(false);
    } catch {
      if (!background) setError(true);   // background errors stay silent
    } finally {
      setLoadingMore(false);
      if (!background) setLoading(false);
    }
  }

  useEffect(() => {
    if (!id) { setEvents([]); setNextCursor(null); setLoading(false); return; }
    const cached = streamCache.get(`${scope}:${id}`);
    if (cached && cached.length) { setEvents(cached); setLoading(false); void load(null, true); }  // instant + silent revalidate
    else { setEvents([]); void load(null, false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, scope]);

  // Auto-sync (governance §0a): while the Activity Section is open, refetch on a
  // light interval + on tab focus, so A/B/C see each other's events without a
  // manual browser refresh. The component mounts only while the Activity tab is
  // open, so the poll is naturally scoped to "while the section is open".
  useEffect(() => {
    if (!id) return;
    // background=true → silent: no loading flash, no list clear, prepend-only.
    const refetch = () => { if (document.visibilityState !== "hidden") void load(null, true); };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", refetch);
    const poll = window.setInterval(refetch, 12000);   // calm V1 interval
    return () => {
      window.removeEventListener("focus", refetch);
      document.removeEventListener("visibilitychange", refetch);
      window.clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, scope]);

  // Real-time push (governance §0a): subscribe to the SSE activity channel and
  // refetch SILENTLY the instant a ping for THIS folder/pack arrives — no manual
  // refocus, no 12s wait. The interval poll above stays only as a backstop.
  useEffect(() => {
    if (!id) return;
    let es: EventSource | null = null;
    let cancelled = false;
    void (async () => {
      const { getFreshToken } = await import("./auth.js");
      const token = await getFreshToken();
      if (!token || cancelled) return;
      es = new EventSource(`/api/studio/activity/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => {
        try {
          const ping = JSON.parse(ev.data) as { folderId: string | null; packId: string | null };
          const mine = scope === "pack" ? ping.packId === id : ping.folderId === id;
          if (mine && document.visibilityState !== "hidden") void load(null, true);
        } catch { /* ignore */ }
      };
      // EventSource auto-reconnects on error; the backstop poll covers any gap.
    })();
    return () => { cancelled = true; es?.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, scope]);

  // No active context (folder not selected, or pack has no id).
  if (!id) {
    return <div className="sf-empty-state">{scope === "pack"
      ? (isZh(lang) ? "暂无该款号的活动" : "No pack activity yet")
      : (isZh(lang) ? "选择一个共享文件夹查看其活动" : "Select a shared folder to view its activity")}</div>;
  }
  if (loading) {
    return <div className="sf-empty-state">{isZh(lang) ? "正在加载活动…" : "LOADING ACTIVITY…"}</div>;
  }
  if (error && events.length === 0) {
    return (
      <div style={{ padding: "8px 0" }}>
        <div style={{ fontSize: 10, color: "#e05555", marginBottom: 6 }}>{isZh(lang) ? "无法加载活动记录。" : "Couldn't load activity."}</div>
        <button onClick={() => void load(null)} style={{ fontSize: 10, background: "transparent", border: "1px solid #3a3a3a", color: "#ccc", cursor: "pointer", padding: "2px 8px", fontFamily: "var(--font)" }}>
          {isZh(lang) ? "重试" : "RETRY"}
        </button>
      </div>
    );
  }
  if (events.length === 0) {
    return <div className="sf-empty-state">{scope === "pack"
      ? (isZh(lang) ? "暂无该款号的活动" : "No pack activity yet")
      : (isZh(lang) ? "暂无活动记录" : "NO ACTIVITY YET")}</div>;
  }

  return (
    <>
      {events.map(e => {
        const color = e.actor_color ?? "#888";
        return (
          <div key={e.id} className="sf-activity-item">
            <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0, marginTop: 4 }} />
              <span style={{ fontSize: 11, lineHeight: 1.5, color: "#9a9a9a", wordBreak: "break-word" }}>
                <span style={{ fontWeight: 700, color }}>{actorLabel(e, lang)}{e.actor_is_viewer && e.actor_name ? (isZh(lang) ? "（你）" : " (you)") : ""}</span>
                {" "}{predicate(e, lang)}
              </span>
            </div>
            <div className="sf-activity-time">
              {e.system_seq != null ? `#${e.system_seq} · ` : ""}{formatTime(e, lang)}
            </div>
          </div>
        );
      })}
      {nextCursor && (
        <button onClick={() => void load(nextCursor)} disabled={loadingMore}
          style={{ marginTop: 6, fontSize: 10, background: "transparent", border: "1px solid #3a3a3a", color: loadingMore ? "#444" : "#ccc", cursor: loadingMore ? "default" : "pointer", padding: "2px 8px", fontFamily: "var(--font)" }}>
          {loadingMore ? "…" : (isZh(lang) ? "加载更多" : "LOAD MORE")}
        </button>
      )}
      {error && events.length > 0 && (
        <div style={{ fontSize: 10, color: "#e05555", marginTop: 6 }}>{isZh(lang) ? "加载更多失败。" : "Couldn't load more."}</div>
      )}
    </>
  );
}
